/**
 * Optimistic Updates Utility
 * 
 * This utility provides helpers for implementing optimistic updates with React Query,
 * improving perceived performance by updating the UI immediately before the API call completes.
 */

import { toast } from 'react-toastify';

/**
 * Create an optimistic mutation with automatic rollback on error
 * @param {Object} queryClient - React Query client instance
 * @param {string|Array} queryKey - Query key to update optimistically
 * @param {Function} updateFn - Function to update the cached data optimistically
 * @param {Object} options - Additional options
 * @returns {Object} Mutation configuration object
 */
export const createOptimisticMutation = (
  queryClient,
  queryKey,
  updateFn,
  options = {}
) => {
  const {
    successMessage,
    errorMessage = 'Operation failed. Please try again.',
    onSuccessCallback,
    onErrorCallback,
    rollbackOnError = true
  } = options;

  return {
    onMutate: async (variables) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries(queryKey);

      // Snapshot the previous value
      const previousData = queryClient.getQueryData(queryKey);

      // Optimistically update to the new value
      if (updateFn && previousData) {
        queryClient.setQueryData(queryKey, (old) => updateFn(old, variables));
      }

      // Return a context object with the snapshotted value
      return { previousData };
    },

    onSuccess: (data, variables, context) => {
      // Show success message if provided
      if (successMessage) {
        toast.success(successMessage);
      }

      // Call custom success callback
      if (onSuccessCallback) {
        onSuccessCallback(data, variables, context);
      }

      // Invalidate and refetch to ensure we have the latest data
      queryClient.invalidateQueries(queryKey);
    },

    onError: (error, variables, context) => {
      // Rollback to the previous value on error
      if (rollbackOnError && context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData);
      }

      // Show error message
      toast.error(errorMessage);

      // Call custom error callback
      if (onErrorCallback) {
        onErrorCallback(error, variables, context);
      }
    },

    onSettled: () => {
      // Always refetch after error or success to ensure consistency
      queryClient.invalidateQueries(queryKey);
    }
  };
};

/**
 * Common optimistic update patterns
 */
export const OPTIMISTIC_PATTERNS = {
  // Add item to list
  ADD_TO_LIST: (list, newItem) => [...(list || []), newItem],
  
  // Remove item from list by ID
  REMOVE_FROM_LIST: (list, itemId) => 
    (list || []).filter(item => item.id !== itemId),
  
  // Update item in list by ID
  UPDATE_IN_LIST: (list, updatedItem) =>
    (list || []).map(item => 
      item.id === updatedItem.id ? { ...item, ...updatedItem } : item
    ),
  
  // Toggle boolean property
  TOGGLE_PROPERTY: (item, property) => ({
    ...item,
    [property]: !item[property]
  }),
  
  // Increment/decrement number
  INCREMENT: (item, property, amount = 1) => ({
    ...item,
    [property]: (item[property] || 0) + amount
  }),
  
  // Update nested object
  UPDATE_NESTED: (item, path, value) => {
    const keys = path.split('.');
    const result = { ...item };
    let current = result;
    
    for (let i = 0; i < keys.length - 1; i++) {
      current[keys[i]] = { ...current[keys[i]] };
      current = current[keys[i]];
    }
    
    current[keys[keys.length - 1]] = value;
    return result;
  }
};

/**
 * Helper for wallet balance updates
 */
export const createWalletBalanceUpdate = (queryClient, walletId, balanceChange) => {
  return createOptimisticMutation(
    queryClient,
    ['fetchWallet'],
    (wallets) => wallets.map(wallet => 
      wallet.id === walletId 
        ? { 
            ...wallet, 
            balance: (parseFloat(wallet.balance) + balanceChange).toString(),
            available_balance: (parseFloat(wallet.available_balance) + balanceChange).toString()
          }
        : wallet
    ),
    {
      successMessage: 'Balance updated successfully',
      errorMessage: 'Failed to update balance'
    }
  );
};

/**
 * Helper for order status updates
 */
export const createOrderStatusUpdate = (queryClient, orderId, newStatus) => {
  return createOptimisticMutation(
    queryClient,
    ['fetchOrder'],
    (orders) => orders.map(order => 
      order.id === orderId 
        ? { ...order, status: newStatus }
        : order
    ),
    {
      successMessage: 'Order updated successfully',
      errorMessage: 'Failed to update order'
    }
  );
};

/**
 * Helper for adding new transactions
 */
export const createTransactionAdd = (queryClient, newTransaction) => {
  return createOptimisticMutation(
    queryClient,
    ['fetchTransaction'],
    (transactions) => [newTransaction, ...(transactions || [])],
    {
      successMessage: 'Transaction added successfully',
      errorMessage: 'Failed to add transaction'
    }
  );
};

export default {
  createOptimisticMutation,
  OPTIMISTIC_PATTERNS,
  createWalletBalanceUpdate,
  createOrderStatusUpdate,
  createTransactionAdd
};
